import type { User, UserStatus } from '@repo-level/core-front';
import { findBoolean } from '@repo-level/core-front';
import { curry, equals, map, pipe, prop, propOr } from 'ramda';

export enum USER_GROUP {
  ONE_OR_MORE_DEP = 'DEP>0',
  WELCOME_PACKAGE = 'WP',
  SIGN_UPS = '00011',
  VIP = 'VIP',
  GOLD = 'VIP1',
  PLATINUM = 'VIP2',
  DIAMOND = 'VIP3',
  BA = 'BA',
  BH = 'BH',
  BR = 'BR',
  LA = 'LA',
  BR2 = 'BR2',
  ACTIVE_BONUS = 'ACTBONUS',
  NO_KYC = 'NO_KYC',
  VERIFIED = 'Tag_Verified',
  DEV_TEST = 'DevTest', //group for QA
  ASKED_FOR_DOCS = 'asked_docs',
  VIPBA = 'VIPBA',
  VIPBH = 'VIPBH',
  LOYALS = 'Loyals',
  CHAMPIONS = 'Champions',
  PVIP = 'PVIP',
  SDR_COMPLETE = 'SDR_COMPLETE',
  FIAT_PLAYERS = 'FiatPlayers',
  DUPLICATE = 'Duplicate',
  LIBERGOS = 'libergos',
  DEP4 = 'DEP4',
  PENNY_SLOTS = 'Penny_Slots',
  LOW_BET_LIMIT = 'low_bet_limit',
  Bonanza_GROUP_A = 'group_a',
  Bonanza_GROUP_B = 'group_b',
  Bonanza_GROUP_C = 'group_c',
  Bonanza_GROUP_D = 'group_d',
  Bonanza_GROUP_E = 'group_e',
  HIGHER_CASHOUT_LIMITS = 'higher_cashout_limits_v1',
  // Documents
  ID_REQUESTED = 'id_requested',
  ADDRESS_REQUESTED = 'address_requested',
  PAYMENT_REQUESTED = 'payment_requested',
  ID_VERIFIED = 'id_verified',
  ADDRESS_VERIFIED = 'address_verified',
  PAYMENT_VERIFIED = 'payment_verified',
  // Deps amount
  DEP1 = '1DEP', // min 1 - max 2
  DEP2 = '2DEP', // min 2 - max 3
  DEP3 = '3DEP', // min 3 - max 4
  DEPS4 = '4DEP', // min 4 - max 5,
  DEPOSITOR = 'DEP', // 5+ deps,
  // Risk Segments
  RISK_SEGMENT_S1 = 'risk-segment:S1',
  RISK_SEGMENT_S2 = 'risk-segment:S2',
  RISK_SEGMENT_S3 = 'risk-segment:S3',
  RISK_SEGMENT_S4 = 'risk-segment:S4',
  RISK_SEGMENT_S5 = 'risk-segment:S5',
  RISK_SEGMENT_S6 = 'risk-segment:S6',
  RISK_SEGMENT_S7 = 'risk-segment:S7',
  RISK_SEGMENT_S8 = 'risk-segment:S8',
  RISK_SEGMENT_S9 = 'risk-segment:S9',
  RISK_SEGMENT_S10 = 'risk-segment:S10',
}

export const COMMON_VIP_GROUPS = [
  USER_GROUP.GOLD,
  USER_GROUP.PLATINUM,
  USER_GROUP.DIAMOND,
];

export const vipGroups = [...COMMON_VIP_GROUPS, USER_GROUP.PVIP];

export const DEFAULT_NEW_USER_GROUPS = [
  { name: 'WPE', id: USER_GROUP.WELCOME_PACKAGE },
  { name: 'Sign-ups', id: USER_GROUP.SIGN_UPS },
];

export const BONUS_RESTRICTED_GROUPS = [{ name: 'BR', id: USER_GROUP.BR }];

export const bonusRestrictedGroups = [USER_GROUP.BA, USER_GROUP.BR];

export const BonanzaRestrictedGroups = [
  USER_GROUP.BR,
  USER_GROUP.RISK_SEGMENT_S1,
  USER_GROUP.RISK_SEGMENT_S2,
  USER_GROUP.RISK_SEGMENT_S3,
];

export const groupMember = (groupId: string) =>
  pipe<User | undefined, UserStatus[], UserStatus['id'][], boolean>(
    propOr<UserStatus[]>([], 'statuses'),
    map(prop('id')),
    findBoolean(equals(groupId))
  );

export const everyGroupMember =
  (groupId: string[]) => (user: User | undefined) =>
    map((id) => groupMember(id)(user), groupId).every((v) => !!v);

export const someGroupMember =
  (groupId: string[]) => (user: User | undefined) =>
    map((id) => groupMember(id)(user), groupId).some((v) => !!v);

export const groupMemberDepositor = someGroupMember([USER_GROUP.DEPOSITOR]);

export const groupMemberOneOrMoreDep = someGroupMember([
  USER_GROUP.ONE_OR_MORE_DEP,
]);

export const groupMemberNoDeposits = groupMember(USER_GROUP.SIGN_UPS);

export const groupMemberVip = someGroupMember([USER_GROUP.VIP]);

export const groupMemberCommonVIP = someGroupMember(COMMON_VIP_GROUPS);

export const groupMemberGOLD = groupMember(USER_GROUP.GOLD);

export const groupMemberPLATINUM = groupMember(USER_GROUP.PLATINUM);

export const groupMemberDIAMOND = groupMember(USER_GROUP.DIAMOND);

export const groupMemberHigherCashoutLimits = groupMember(
  USER_GROUP.HIGHER_CASHOUT_LIMITS
);

export const hasHigherCashoutLimits = (user: User | undefined) =>
  groupMemberCommonVIP(user) && groupMemberHigherCashoutLimits(user);

export const groupMemberBA = groupMember(USER_GROUP.BA);

export const groupMemberBH = groupMember(USER_GROUP.BH);

export const groupMemberBR = groupMember(USER_GROUP.BR);

export const groupMemberVIPBA = groupMember(USER_GROUP.VIPBA);

export const groupMemberVIPBH = groupMember(USER_GROUP.VIPBH);

export const groupMemberLA = groupMember(USER_GROUP.LA);

export const groupMemberActiveBonus = groupMember(USER_GROUP.ACTIVE_BONUS);

export const groupMemberNoKYC = groupMember(USER_GROUP.NO_KYC);

export const groupMemberVerified = groupMember(USER_GROUP.VERIFIED);

export const groupMemberFiatPlayers = groupMember(USER_GROUP.FIAT_PLAYERS);

export const groupMemberDuplicate = groupMember(USER_GROUP.DUPLICATE);

export const groupMemberPennySlots = groupMember(USER_GROUP.PENNY_SLOTS);
export const groupMemberDEP4 = groupMember(USER_GROUP.DEP4);

export const pickGroupsCurried = curry(
  (vipGroups: USER_GROUP[], user: User | undefined) =>
    vipGroups.filter((group) => groupMember(group)(user))
);

export const pickVipGroupsCurried = pickGroupsCurried(COMMON_VIP_GROUPS);

export const groupMemberIdRequested = groupMember(USER_GROUP.ID_REQUESTED);

export const groupMemberAddressRequested = groupMember(
  USER_GROUP.ADDRESS_REQUESTED
);

export const groupMemberPaymentRequested = groupMember(
  USER_GROUP.PAYMENT_REQUESTED
);

export const groupMemberIdVerified = groupMember(USER_GROUP.ID_VERIFIED);

export const groupMemberAddressVerified = groupMember(
  USER_GROUP.ADDRESS_VERIFIED
);

export const groupMemberPaymentVerified = groupMember(
  USER_GROUP.PAYMENT_VERIFIED
);

export const groupMemberDocumentsRequested = someGroupMember([
  USER_GROUP.ID_REQUESTED,
  USER_GROUP.ADDRESS_REQUESTED,
  USER_GROUP.PAYMENT_REQUESTED,
]);

export const groupMemberDocumentsVerified = everyGroupMember([
  USER_GROUP.ID_VERIFIED,
  USER_GROUP.ADDRESS_VERIFIED,
  USER_GROUP.PAYMENT_VERIFIED,
]);
